import { Body, H5, H7, Label } from '@qred/components-library';
import React from 'react';
import { useSelector } from 'react-redux';
import Button, { ButtonType } from '~/components/shared/Button/button';
import Divider from '~/components/shared/Divider/Divider';
import Icon, { SvgSrc } from '~/components/shared/Icon/Icon';
import Group from '~/components/shared/Layout/Group';
import Stack from '~/components/shared/Layout/Stack';
import { marketHasBrokerDataSharingDisclosure } from '~/constants/markets';
import { LoanType, PartnerType } from '~/enums';
import { numberOfBusinessDaysFromToday } from '~/helpers/getNextWorkingDay.helper';
import { getMonthlyPaymentValues } from '~/helpers/paymentPlan.helper';
import { useCurrencyFormatter } from '~/hooks/formatter/useCurrencyFormatter';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useDateFormatter from '~/hooks/useDateFormatter';
import useModals from '~/hooks/useModals';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { useGetPaymentPlanQuery } from '~/store/apis/endpoints/onboardingApi/onboarding.api';
import { useLoanOfferSelectors } from '~/store/selectors/loanOffer.selector';
import { ComponentSize } from '~/styles/BaseStyledComponentsOld';
import PaymentCalculatorModal from '../PaymentCalculator/PaymentCalculatorModal';
import PersonalLiability from '../PersonalLiability/PersonalLiability';
import TermsAndConditions from '../TermsAndConditions/TermsAndConditions';
import { BottomContent, SummaryHeader } from './ContractSummary.styled';

const ContractSummary = () => {
  const translate = useTranslate();
  const dispatch = useAppDispatch();
  const modals = useModals();

  const formatDate = useDateFormatter();
  const currencyFormatter = useCurrencyFormatter();

  const {
    loanOffer: { form, overview },
    intl: { market },
    matchedMedia,
  } = useSelector((state: RootState) => state);

  const { loanAmount } = useLoanOfferSelectors();

  const { data: paymentPlanData, isFetching } = useGetPaymentPlanQuery({
    applicationUuid: overview.applicationUuid,
    loanAmount,
    loanTerm: form.loanPeriod,
  });

  const {
    monthlyAmortization,
    monthlyFee,
    monthlyPayment,
  } = getMonthlyPaymentValues(paymentPlanData);

  const payoutDate = new Date(numberOfBusinessDaysFromToday(market, 1));

  const handleOpenPaymentScheduleModal = () => {
    dispatch(
      pushToGtmLoanAction({
        actionName: 'loan_offer_payment_plan_open',
        stepActionNumber: 0.1,
      })
    );

    modals.openModal({
      header: <H7>{translate('PaymentCalculator.PaymentSchedule')}</H7>,
      children: (
        <PaymentCalculatorModal
          applicationUuid={overview.applicationUuid}
          amount={loanAmount}
          loanTerm={form.loanPeriod}
        />
      ),
      footer: (
        <Button type={ButtonType.Outlined} onClick={modals.closeAll}>
          {translate('Ok')}
        </Button>
      ),
      fullScreen: matchedMedia.smallScreenPortrait,
      fixedHeaderAndFooter: matchedMedia.smallScreenPortrait,
      filledBackground: true,
      size: 'xl',
    });
  };

  return (
    <Stack spacing="lg" grow>
      <SummaryHeader>
        <Stack align="center" spacing="md">
          <Stack spacing="sm">
            <Label size="sm">{translate('OnboardingOffer.QredPaysYou')}</Label>
            <H5>{currencyFormatter.format(form.payoutAmount)}</H5>
          </Stack>
          <Body size="md">
            {formatDate(payoutDate, {
              month: 'long',
              day: '2-digit',
              year: 'numeric',
            })}
          </Body>
        </Stack>
      </SummaryHeader>

      <Stack justify="space-between">
        {!!overview.currentLoanAmountLeft && (
          <Stack spacing="xs">
            <Group justify="space-between">
              <Body size="lg">{translate('TotalLoan')}</Body>
              <Body size="lg">{currencyFormatter.format(loanAmount)}</Body>
            </Group>
            <Group justify="space-between">
              <Body size="lg">{translate('OnboardingOffer.LoanTerms')}</Body>
              <Body size="lg">
                {`${form.loanPeriod} ${translate('Months')}`}
              </Body>
            </Group>
          </Stack>
        )}

        <Stack spacing="sm">
          {overview.loanType === LoanType.FLEX_LOAN && (
            <Stack spacing="xs">
              <Group justify="space-between">
                <Body size="lg">{translate('MonthlyAmortization')}</Body>
                <Body
                  size="lg"
                  style={{
                    opacity: isFetching ? 0.5 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {monthlyAmortization !== undefined &&
                    currencyFormatter.format(monthlyAmortization)}
                </Body>
              </Group>
              <Group justify="space-between">
                <Body size="lg">{translate('MonthlyFee')}</Body>
                <Body
                  size="lg"
                  style={{
                    opacity: isFetching ? 0.5 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {monthlyFee !== undefined &&
                    currencyFormatter.format(monthlyFee)}
                </Body>
              </Group>
            </Stack>
          )}
          <Group justify="space-between">
            <H7>{translate('OnboardingOffer.YouPayQred')}</H7>
            <H7
              style={{
                opacity: isFetching ? 0.5 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              {monthlyPayment !== undefined &&
                currencyFormatter.format(monthlyPayment)}
            </H7>
          </Group>
        </Stack>
      </Stack>
      <Stack>
        <Button
          type={ButtonType.Tertiary}
          onClick={handleOpenPaymentScheduleModal}
        >
          <Group align="center" justify="center" spacing="sm">
            <Icon src={SvgSrc.CalendarDay} size={ComponentSize.Small} />
            {translate('ViewPaymentSchedule')}
          </Group>
        </Button>
        <Divider />
      </Stack>

      <BottomContent spacing="sm">
        <PersonalLiability />
        <TermsAndConditions />
        {marketHasBrokerDataSharingDisclosure[market] &&
          overview.partnerType === PartnerType.BROKER && (
            <Body size="sm">
              {translate('OnboardingOffer.BrokerDataSharingConsent')}
            </Body>
          )}
      </BottomContent>
    </Stack>
  );
};

export default ContractSummary;
